import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { injectIntl, FormattedMessage, FormattedNumber } from 'react-intl';
import SignalRService from '@/shared/services/SignalRService';
import intlShape from '@/shared/utils/intlShape';

import FormattedCurrency from '@/shared/components/FormattedCurrency';
import FormattedListValue from '@/shared/components/FormattedListValue';
import RelationName from '@/shared/components/RelationName';
import SimplePanel from '@/shared/components/SimplePanel';
import YesNoModal from '@/shared/components/YesNoModal';

import {
  FaFileExcel,
  FaShareAlt,
  FaFileMedical,
  FaPlus,
  FaExternalLinkAlt,
  FaCog,
} from 'react-icons/fa';

import moment from 'moment';
import Axios from 'axios';
import api from '@/shared/api';
import config from '@/config';
import { CanCreate, CanUpdate, CanRead } from '@/shared/components/security';
import { injectStateService, StateService } from '@/shared/services/StateService';
import Button from '@/shared/components/Button';
import handleHttpError from '@/shared/services/HandleHttpError';
import ToastrService from '@/shared/services/ToastrService';
import Checkbox from '@/shared/components/input/Checkbox';
import DataCacheService from '@/shared/services/DataCacheService';
import RegistrationModal from '@/shared/components/registration/RegistrationModal';
import { createEmptyRegistration } from '@/shared/components/registration/registrationHelpers';
import { mapInvoiceStreamName } from '@/shared/services/ObjectDisplayService';
import ScrollableTable from '@/shared/components/table/ScrollableTable';
import ColumnHeader from '@/shared/components/table/ColumnHeader';
import ToInvoiceDetail from './ToInvoiceDetail';
import ToInvoiceFilters, { defaultFilters } from './ToInvoiceFilters';
import ToInvoiceGlobalActionsButton from './ToInvoiceGlobalActionsButton';
import messages from '../invoicingMessages';
import FormattedDateShort from '../../../shared/components/FormattedDateShort';
import {
  countVisibleColumns,
  handleHiddenColumnsSelection,
  initHiddenColumnsState,
} from '@/shared/utils/hiddenColumnsHelper';
import CheckboxListModal from '@/shared/components/CheckboxListModal';

const columnSelectionStorageKey = 'ToInvoiceHiddenColumns';

const showColumn = {
  distance: item => item.distance !== 0,
  inForfaitAmount: item =>
    item.invoicingType === 1 && item.inForfaitAmount !== null && item.inForfaitAmount > 0,
  exForfaitAmount: item =>
    item.invoicingType === 1 && item.exForfaitAmount !== null && item.exForfaitAmount > 0,
  fixedAmount: item =>
    item.invoicingType === 1 && item.fixedAmount !== null && item.fixedAmount > 0,
  unsettledAdvances: item => item.unsettledAdvances !== null && item.unsettledAdvances > 0,
};
const editRegistrationOptions = {
  editableRelation: true,
};
const formatFiltersForRequest = ({ dateFrom, dateUntil, ...rest } = {}) => ({
  ...rest,
  dateFrom: dateFrom ? moment(dateFrom).format('YYYY-MM-DD') : undefined,
  dateUntil: dateUntil ? moment(dateUntil).format('YYYY-MM-DD') : undefined,
});

const SessionStorageKey = 'ToInvoiceParams';

class ToInvoiceContainer extends Component {
  constructor(props) {
    super(props);
    const { searchParams = {}, intl } = props;
    const { toInvoiceFilters = {}, ...otherSessionParams } =
      JSON.parse(sessionStorage.getItem(SessionStorageKey)) || {};

    if (toInvoiceFilters.dateFrom) {
      toInvoiceFilters.dateFrom = moment.utc(toInvoiceFilters.dateFrom);
    }
    if (toInvoiceFilters.dateUntil) {
      toInvoiceFilters.dateUntil = moment.utc(toInvoiceFilters.dateUntil);
    }

    const assignmentsEnabled = DataCacheService.assignmentsEnabled();
    const columnDefinitions = [
      { id: 'relationCode', message: messages.RelationCode, visibleByDefault: false },
      assignmentsEnabled && {
        id: 'assignment',
        message: messages.Assignment,
        visibleByDefault: true,
      },
    ].filter(x => !!x);

    const hiddenColumnsState = initHiddenColumnsState(
      columnSelectionStorageKey,
      columnDefinitions,
      intl,
    );

    this.state = {
      items: [],
      ...hiddenColumnsState,
      assignmentsEnabled,
      invoiceScheduleDate: moment.utc(),
      invoiceScheduleDateFocused: false,
      hideDevPanel: true,
      allRelationsSelected: false,
      showColumnSelectionModal: false,
      details: {},
      globalActionRunning: false,
      totals: {},
      initialSearchParams: {
        page: 0,
        sortKey: 'displayName',
        pageSize: 50,
        ...otherSessionParams,
        toInvoiceFilters: {
          ...defaultFilters,
          ...toInvoiceFilters,
          ...searchParams,
        },
      },
    };
  }

  loadItems = searchParams => {
    const toInvoiceFilters = formatFiltersForRequest(searchParams.toInvoiceFilters);

    const params = {
      desc: searchParams.desc,
      sort: searchParams.sortKey,
      page: searchParams.page,
      take: searchParams.pageSize,
      ...toInvoiceFilters,
    };

    const sessionParams = JSON.stringify({
      desc: searchParams.desc,
      sortKey: searchParams.sortKey,
      toInvoiceFilters: searchParams.toInvoiceFilters,
    });

    if (params.page === 0) this.retrieveTotals(toInvoiceFilters);

    sessionStorage.setItem(SessionStorageKey, sessionParams);

    if (this.token && !this.token.token.reason) {
      this.token.cancel('Operation canceled due to new request.');
    }
    this.token = Axios.CancelToken.source();

    return api.base
      .get(`${config.appUrls.api}/api/Invoice/CustomerBillable`, {
        params,
        cancelToken: this.token.token,
      })
      .then(({ data }) => {
        this.setState(({ allRelationsSelected, items, details }) => {
          const newItems = data.map(e => ({ ...e, selected: !!allRelationsSelected }));
          return {
            items: searchParams.page > 0 ? [...items, ...newItems] : newItems,
            details: searchParams.page === 0 ? {} : details,
          };
        });

        return data;
      })
      .catch(error => {
        if (!Axios.isCancel(error)) return error;
      });
  };

  retrieveTotals = filters => {
    if (typeof this.totalsToken !== typeof undefined) {
      this.totalsToken.cancel('Operation canceled due to new request.');
    }
    this.totalsToken = Axios.CancelToken.source();
    return api.base
      .get(`${config.appUrls.api}/api/Invoice/CustomerBillableTotals`, {
        params: { ...filters },
        cancelToken: this.totalsToken.token,
      })
      .then(({ data }) => {
        this.setState({
          totals: data || {},
        });

        return data || {};
      })
      .catch(error => {
        if (!Axios.isCancel(error)) return error;
      });
  };

  retrieveDetails = invoiceStreamId => {
    const { initialSearchParams: { toInvoiceFilters = {} } = {} } = this.state;

    const { dateFrom, dateUntil, userId, resourceId, referenceYear } = toInvoiceFilters;
    const params = formatFiltersForRequest({
      dateFrom,
      dateUntil,
      userId,
      resourceId,
      referenceYear,
    });
    return api.base
      .get(`${config.appUrls.api}/api/Invoice/CustomerBillableDetails/${invoiceStreamId}`, {
        params,
      })
      .then(({ data }) => {
        const { details } = this.state;
        const detail = { ...(details[invoiceStreamId] || { collapsed: false }), data };

        this.setState({
          details: { ...details, [invoiceStreamId]: detail },
          showEditRegistrationModal: false,
        });
      })
      .catch(error => {
        const { intl } = this.props;
        handleHttpError(intl)(error);
      });
  };

  componentDidMount() {
    SignalRService.subscribe('draft-registration-invoice-created', this.draftInvoiceCreatedHandler);
  }
  componentWillUnmount() {
    SignalRService.unsubscribe(
      'draft-registration-invoice-created',
      this.draftInvoiceCreatedHandler,
    );

    if (this.token && !this.token.token.reason) {
      this.token.cancel('Operation canceled due to leaving page.');
    }
    if (this.totalsToken && !this.totalsToken.token.reason) {
      this.totalsToken.cancel('Operation canceled due to leaving page.');
    }
  }

  draftInvoiceCreatedHandler = invoices => {
    this.setState(state => {
      if (!state.items) return state;

      return {
        items: state.items.map(i => {
          if (!invoices.find(x => x.invoiceStreamId === i.invoiceStreamId)) return i;
          return {
            ...i,
            openRegistrationInvoiceStartDate: null,
            created: true,
          };
        }),
      };
    });
  };

  reload = () => {
    this.setState(state => ({
      items: [],
      details: {},
      initialSearchParams: {
        ...state.initialSearchParams,
        page: 0,
        r: new Date().getTime(),
      },
    }));
  };
  reloadInvoiceStream = invoiceStreamId => {
    const { items } = this.state;
    if (!items.some(x => x.invoiceStreamId === invoiceStreamId)) return Promise.resolve(null);
    return api.base
      .get(`${config.appUrls.api}/api/Invoice/CustomerBillable`, {
        params: {
          invoiceStreamId,
        },
        cancelToken: this.token.token,
      })
      .then(({ data }) => {
        if (!data || !data.length) return null;
        const [item] = data;
        this.setState(
          state => ({
            items: state.items.map(x =>
              x.invoiceStreamId === item.invoiceStreamId ? { ...x, ...item } : x,
            ),
          }),
          () => {
            const { details } = this.state;
            if (!details[invoiceStreamId]) return;
            this.retrieveDetails(invoiceStreamId);
          },
        );
        return item;
      });
  };

  billSingleRelation = row => {
    const { initialSearchParams: { toInvoiceFilters = {} } = {} } = this.state;
    const { dateFrom, dateUntil } = toInvoiceFilters;
    const params = formatFiltersForRequest({ dateFrom, dateUntil });

    return api.base
      .post(
        `${config.appUrls.api}/api/InvoiceRegistration/BillStream/${row.invoiceStreamId}`,
        params,
      )
      .then(
        ({ data }) => {
          if (data) {
            this.setState(state => ({
              items: state.items
                ? state.items.map(i => {
                    if (i.invoiceStreamId !== row.invoiceStreamId) return i;
                    return { ...i, created: true };
                  })
                : state.items,
            }));
          }
          return true;
        },
        error => {
          const { intl } = this.props;
          handleHttpError(intl)(error);
        },
      );
  };

  getSelectionParams = () => {
    const { items, allRelationsSelected } = this.state;

    // Get all invoiceStream ids that don't match the select all checkbox
    const invoiceStreamIds = items
      ? items.filter(x => x.selected !== allRelationsSelected).map(x => x.invoiceStreamId)
      : [];
    return {
      invoiceStreamIds,
      selectAll: allRelationsSelected,
    };
  };
  billMultipleRelations = () => {
    const { intl, stateService } = this.props;
    const { initialSearchParams: { toInvoiceFilters = {} } = {} } = this.state;
    this.setState({ globalActionRunning: true });

    const model = {
      ...this.getSelectionParams(),
    };

    return api.base
      .post(`${config.appUrls.api}/api/InvoiceRegistration/BatchBill/`, model, {
        params: formatFiltersForRequest(toInvoiceFilters),
      })
      .then(
        ({ data }) => {
          if (data) {
            this.setState(({ items }) => ({
              globalActionRunning: false,
              items: items
                ? items.map(item =>
                    item.selected
                      ? {
                          ...item,
                          openRegistrationInvoiceStartDate:
                            item.openRegistrationInvoiceStartDate || moment.utc(),
                        }
                      : item,
                  )
                : items,
            }));
            ToastrService.success(
              intl.formatMessage(messages.NrDraftInvoicedCreatedOrModified, { nr: data }),
              null,
              () => {
                stateService.gotoDraftInvoices();
              },
            );
          }
        },
        error => {
          handleHttpError(intl)(error);
          this.setState({ globalActionRunning: false });
        },
      );
  };

  handleSelection = row => {
    this.setState(({ items }) => ({
      items: items.map(x =>
        x.invoiceStreamId === row.invoiceStreamId ? { ...x, selected: !x.selected } : x,
      ),
    }));
  };
  getCountSelectedRelations() {
    const { allRelationsSelected, items, totals } = this.state;
    if (!items || !totals) return 0;
    const total = totals.count || 0;
    const count = items.filter(x => x.selected !== allRelationsSelected).length;

    if (allRelationsSelected) {
      return total - count;
    }
    return count;
  }
  getCountSelectedRelationsFormatted() {
    const { items, totals = {} } = this.state;
    const { intl } = this.props;
    const total = items ? totals.count || 0 : 0;
    const count = this.getCountSelectedRelations();

    return intl.formatMessage(messages.CountSelected, { count, total });
  }

  toggleCollapse = invoiceStreamId => {
    const { details } = this.state;
    if (!details[invoiceStreamId]) {
      this.setState(
        { details: { ...details, [invoiceStreamId]: { collapsed: false, data: {} } } },
        () => {
          // load
          this.retrieveDetails(invoiceStreamId);
        },
      );
    } else {
      const detail = { ...details[invoiceStreamId] };
      detail.collapsed = !detail.collapsed;
      this.setState({ details: { ...details, [invoiceStreamId]: detail } });
    }
  };

  billRegistration = (registration, invoiceStreamId) => {
    const { details } = this.state;
    const detail = details[invoiceStreamId];

    this.setState(
      {
        details: {
          ...details,
          [invoiceStreamId]: {
            ...detail,
            data: {
              ...detail.data,
              registrations: detail.data.registrations.map(x => {
                if (registration.id === x.id) {
                  return {
                    ...x,
                    addingToDraft: true,
                  };
                }
                return x;
              }),
            },
          },
        },
      },
      () => {
        api.base.post(`${config.appUrls.api}/api/InvoiceRegistration/post`, [registration.id]).then(
          ({ data }) => {
            if (data) {
              const { intl } = this.props;
              ToastrService.success(
                intl.formatMessage(messages.RegistrationAddedToInvoiceProposal),
              );
              this.setState(
                {
                  details: {
                    ...details,
                    [invoiceStreamId]: {
                      ...detail,
                      data: {
                        ...detail.data,
                        registrations: detail.data.registrations.filter(
                          x => x.id !== registration.id,
                        ),
                      },
                    },
                  },
                },
                () => {
                  // refresh the totals for current invoicestream
                  this.refreshOpenRegistrationsForInvoiceStream(invoiceStreamId);
                },
              );
            }
          },
          error => {
            const { intl } = this.props;
            handleHttpError(intl)(error);
          },
        );
      },
    );
  };

  toggleInForfait = (invoiceStreamId, registration, checked) => {
    const { intl } = this.props;
    if (registration.invoiceStatus > 0) return ''; //Only allowed on registrations not linked to invoice
    return api.base
      .put(`${config.appUrls.api}/api/registration/InForfait/${registration.id}`, null, {
        params: { inForfait: checked },
      })
      .then(({ data }) => {
        this.setState(this.updateRegistrationState(data, invoiceStreamId), () => {
          // refresh the totals for current invoicestream
          this.refreshOpenRegistrationsForInvoiceStream(invoiceStreamId);
        });
      }, handleHttpError(intl));
  };
  toggleBillable = (invoiceStreamId, registration, checked) => {
    const { intl } = this.props;
    if (registration.invoiceStatus > 0) return ''; //Only allowed on registrations not linked to invoice
    return api.base
      .put(`${config.appUrls.api}/api/registration/Billable/${registration.id}`, null, {
        params: { billable: checked },
      })
      .then(({ data }) => {
        this.setState(this.updateRegistrationState(data, invoiceStreamId), () => {
          // refresh the totals for current invoicestream
          this.refreshOpenRegistrationsForInvoiceStream(invoiceStreamId);
        });
      }, handleHttpError(intl));
  };

  renderRow = (
    row,
    {
      showDistance = false,
      showForfait = false,
      showInExForfait = false,
      showAdvances = false,
    } = {},
    intl,
    stateService,
  ) => {
    const isProcessing =
      row.openRegistrationInvoiceStartDate &&
      moment.utc(row.openRegistrationInvoiceStartDate) &&
      moment
        .utc(row.openRegistrationInvoiceStartDate)
        .add(1, 'hours')
        .isAfter(moment.utc());
    const { details, columnStatus, assignmentsEnabled } = this.state;
    const { collapsed = true, data: rowDetail } = details[row.invoiceStreamId] || {};
    const nrOfColumns =
      8 +
      countVisibleColumns(columnStatus) +
      showDistance +
      showForfait +
      showInExForfait +
      showInExForfait +
      showAdvances;
    const { invoiceDialogsSvc } = this.props;

    return (
      <tbody key={row.invoiceStreamId}>
        <tr>
          <td className="text-nowrap">
            <Checkbox onClick={() => this.handleSelection(row)} value={row.selected} />
            <a onClick={() => this.toggleCollapse(row.invoiceStreamId)} className="caret-button">
              <i
                className={classNames('fa pointer', {
                  'fa-caret-up': !collapsed,
                  'fa-caret-down': collapsed,
                })}
              />
            </a>
          </td>
          <td>
            <RelationName
              relation={{
                id: row.relationId,
                type: row.relationType,
                title: row.relationTitle,
                name: row.displayName,
                alias: row.alias,
                isActive: row.isActive,
                relationHandlingLevel: row.relationHandlingLevel,
                relationHandlingLevelClarification: row.relationHandlingLevelClarification,
              }}
            />
            {row.hasInvoicingVia && (
              <span
                className="orange margin10L"
                title={intl.formatMessage(messages.HasInvoicingVia)}
              >
                <FaShareAlt />
              </span>
            )}
          </td>
          {columnStatus.relationCode && <td>{row.customerCode}</td>}
          {assignmentsEnabled && columnStatus.assignment && (
            <td>{mapInvoiceStreamName(row, intl, '')}</td>
          )}
          <td>
            {row.invoicingType === null ? (
              <FormattedMessage {...messages.NoInvoicingSchedule} />
            ) : (
              <span className={classNames({ 'text-danger': !row.invoiceStreamActive })}>
                <FormattedListValue
                  list="invoiceschedule"
                  id={row.invoiceScheduleId}
                  fallback={row.invoiceScheduleName}
                />
                &nbsp;(
                <FormattedListValue list="invoicingtype" id={row.invoicingType} />)
              </span>
            )}
          </td>
          <td>{row.nextScheduleDate && <FormattedDateShort value={row.nextScheduleDate} />}</td>
          <td className="text-right">
            <FormattedCurrency value={row.amount} />
          </td>
          <td className="text-right">
            {' '}
            {!row.hours && row.hours !== 0 ? null : row.hours.toTime()}
          </td>
          {showDistance && (
            <td className="text-right">
              {showColumn.distance(row) && <FormattedNumber value={row.distance} />}
            </td>
          )}
          {showInExForfait && (
            <td className="text-right">
              {showColumn.inForfaitAmount(row) && <FormattedCurrency value={row.inForfaitAmount} />}
            </td>
          )}
          {showInExForfait && (
            <td className="text-right">
              {showColumn.exForfaitAmount(row) && <FormattedCurrency value={row.exForfaitAmount} />}
            </td>
          )}
          {showForfait && (
            <td className="text-right">
              {showColumn.fixedAmount(row) && <FormattedCurrency value={row.fixedAmount} />}
            </td>
          )}
          {showAdvances && (
            <td className="text-right">
              {showColumn.unsettledAdvances(row) && (
                <FormattedCurrency value={row.unsettledAdvances} />
              )}
            </td>
          )}
          <td className="text-right">
            <FormattedCurrency value={row.totalAmount} />
          </td>
          <td className="text-right actions-column">
            {isProcessing && (
              <button disabled type="button" className="btn btn-inverse add-button btn-sm">
                <i className="fa fa-spinner fa-spin margin5R" />
                <FormattedMessage {...messages.Processing} />
              </button>
            )}

            {row.created && !isProcessing && (
              <CanRead on="InvoiceDraft">
                <button
                  type="button"
                  className="btn btn-success add-button btn-sm"
                  onClick={() =>
                    stateService.gotoDraftInvoices({
                      relation: { id: row.relationId, name: row.name },
                    })
                  }
                >
                  <FormattedMessage {...messages.OpenInvoiceProposal} />
                </button>
              </CanRead>
            )}
            {!row.created && !isProcessing && row.hasDraft && (
              <CanUpdate on="InvoiceDraft">
                <Button
                  showProgress
                  className="btn btn-inverse add-button btn-sm margin5R"
                  title={intl.formatMessage(messages.AddToInvoiceProposal)}
                  onClick={() => this.billSingleRelation(row)}
                >
                  <FaFileMedical className="react-icon" />
                  <FormattedMessage {...messages.AddToInvoiceProposal} />
                </Button>
              </CanUpdate>
            )}
            {!row.created && !isProcessing && !row.hasDraft && (
              <CanCreate on="InvoiceDraft">
                <Button
                  showProgress
                  className="btn btn-inverse add-button btn-sm margin5R"
                  title={intl.formatMessage(messages.MakeInvoiceProposal)}
                  onClick={() => this.billSingleRelation(row)}
                >
                  <FaPlus className="react-icon" />
                  <FormattedMessage className="text" {...messages.MakeInvoiceProposal} />
                </Button>
              </CanCreate>
            )}
            <CanRead on="InvoiceDraft">
              <a
                title={intl.formatMessage(messages.ShowInvoiceHistory)}
                className="btn btn-xs btn-icon"
                onClick={() =>
                  invoiceDialogsSvc.showInvoicingHistory(row.invoiceStreamId, null, row.relationId)
                }
              >
                <i className="fa fa-history" />
              </a>
            </CanRead>
            <a
              title={intl.formatMessage(messages.ShowInRegistrationOverview)}
              className="btn btn-xs btn-icon"
              onClick={() =>
                stateService.gotoRegistrationOverview({
                  relation: { id: row.relationId },
                  period: 'all',
                  filters: { invoiceStatus: { items: [0] } },
                  finalInvoiceStreamId: row.invoiceStreamId,
                })
              }
            >
              <FaExternalLinkAlt />
            </a>
          </td>
        </tr>
        {!collapsed && !!rowDetail && (
          <ToInvoiceDetail
            stateService={stateService}
            columns={nrOfColumns}
            invoiceStreamId={row.invoiceStreamId}
            onEditRegistration={registration => {
              this.setState({
                activeRegistration: createEmptyRegistration(registration),
                activeInvoiceStreamId: row.invoiceStreamId,
                showEditRegistrationModal: true,
              });
            }}
            onAddToDraftInvoice={registration =>
              this.billRegistration(registration, row.invoiceStreamId)
            }
            onNeverInvoiceRegistration={registration =>
              this.setState({
                activeRegistration: registration,
                activeInvoiceStreamId: row.invoiceStreamId,
                showNeverInvoiceRegistrationModal: true,
              })
            }
            onRemoveRegistration={registration =>
              this.setState({
                activeRegistration: registration,
                activeInvoiceStreamId: row.invoiceStreamId,
                showRemoveRegistrationModal: true,
              })
            }
            onToggleInForfait={this.toggleInForfait}
            onToggleBillable={this.toggleBillable}
            {...rowDetail}
          />
        )}
      </tbody>
    );
  };

  handleSelectAllRelations = () => {
    const { items, allRelationsSelected } = this.state;

    const isCurrentlySelected = !allRelationsSelected;

    const itemsCopy = items.map(e => ({ ...e, selected: !!isCurrentlySelected }));

    this.setState(state => ({
      ...state,
      allRelationsSelected: isCurrentlySelected,
      items: itemsCopy,
    }));
  };

  isGlobalActionsDisabled = () => {
    const { globalActionRunning } = this.state;
    return globalActionRunning || this.getCountSelectedRelations() === 0;
  };

  filtersChanged = filters => {
    this.setState(state => ({
      items: null,
      initialSearchParams: {
        ...state.initialSearchParams,
        page: 0,
        toInvoiceFilters: filters,
      },
    }));
  };

  createExcelReport = () => {
    const { intl } = this.props;
    const { initialSearchParams: { toInvoiceFilters = {} } = {} } = this.state;

    const culture = intl.locale;

    const url = `/api/invoice/CustomerBillableReport`;

    const urlParams = {
      ...formatFiltersForRequest(toInvoiceFilters),
      culture,
      connectionId: SignalRService.getConnectionId(),
    };

    this.setState({
      creatingReport: true,
    });

    return api.base
      .post(url, null, { params: urlParams })
      .then(() => {
        ToastrService.info(intl.formatMessage(messages.ExportToExcelStarted));

        this.setState({
          creatingReport: false,
        });
      })
      .catch(error => {
        this.setState({
          creatingReport: false,
        });
        handleHttpError(intl)(error);
      });
  };

  onRegistrationModified = registration => {
    const { details, activeRegistration, activeInvoiceStreamId } = this.state;
    const detail = details[activeInvoiceStreamId];

    // if the assignment/invoicestream was changed, remove the registration from current assignment and add to other one
    if (activeRegistration.invoiceStreamId !== registration.invoiceStreamId) {
      this.setState(
        {
          showEditRegistrationModal: false,
        },
        () => {
          // Reload both lines
          Promise.all([
            this.reloadInvoiceStream(activeRegistration.invoiceStreamId),
            this.reloadInvoiceStream(registration.invoiceStreamId),
          ]).then(([i1, i2]) => {
            if (!i1 || !i2) {
              this.reload();
            }
          });
        },
      );
    } else {
      this.setState(
        {
          showEditRegistrationModal: false,
          details: {
            ...details,
            [activeInvoiceStreamId]: {
              ...detail,
              data: {
                ...detail.data,
                registrations: detail.data.registrations.map(x => {
                  if (x.id === registration.id) {
                    return registration;
                  }
                  return x;
                }),
              },
            },
          },
        },
        () => {
          // refresh the totals for current invoicestream
          this.refreshOpenRegistrationsForInvoiceStream(activeInvoiceStreamId);
        },
      );
    }
  };

  refreshOpenRegistrationsForInvoiceStream = invoiceStreamId =>
    api.base
      .get(`${config.appUrls.api}/api/Invoice/CustomerBillable`, {
        params: {
          invoiceStreamId,
        },
      })
      .then(({ data }) => {
        if (data && data.length > 0) {
          const updatedItem = data[0];
          this.setState(state => ({
            items: state.items.map(x => {
              if (x.invoiceStreamId === invoiceStreamId) {
                return updatedItem;
              }
              return x;
            }),
          }));
        }
        if (data.length === 0) {
          // no registrations, delete from list
          this.setState(state => ({
            items: state.items.filter(x => x.invoiceStreamId !== invoiceStreamId),
          }));
        }
      });

  handleColumnSelection = items => {
    this.setState(({ hideableColumns }) => ({
      ...handleHiddenColumnsSelection(items, hideableColumns, columnSelectionStorageKey),
      showColumnSelectionModal: false,
    }));
  };

  removeRegistration = registration => {
    const { details, activeInvoiceStreamId } = this.state;
    const detail = details[activeInvoiceStreamId];
    const model = {
      ...registration,
      removing: true,
    };
    this.setState(this.updateRegistrationState(model, activeInvoiceStreamId), () => {
      api.base
        .delete(
          `${config.appUrls.api}/api/registration/Delete/${registration.id}?deleteGroup=false`,
        )
        .then(
          () => {
            const { intl } = this.props;
            ToastrService.success(intl.formatMessage(messages.RegistrationRemoved));
            this.setState(
              {
                details: {
                  ...details,
                  [activeInvoiceStreamId]: {
                    ...detail,
                    data: {
                      ...detail.data,
                      registrations: detail.data.registrations.filter(
                        x => x.id !== registration.id,
                      ),
                    },
                  },
                },
              },
              () => {
                // refresh the totals for current invoicestream
                this.refreshOpenRegistrationsForInvoiceStream(activeInvoiceStreamId);
              },
            );
          },
          error => {
            const { intl } = this.props;
            this.setState(
              this.updateRegistrationState(
                { ...registration, removing: false },
                activeInvoiceStreamId,
              ),
            );
            handleHttpError(intl)(error);
          },
        );
    });
  };

  updateRegistrationState = (registration, invoiceStreamId) => {
    const { details } = this.state;
    const detail = details[invoiceStreamId];
    return {
      showNeverInvoiceRegistrationModal: false,
      showRemoveRegistrationModal: false,
      showEditRegistrationModal: false,
      details: {
        ...details,
        [invoiceStreamId]: {
          ...detail,
          data: {
            ...detail.data,
            registrations: detail.data.registrations.map(x => {
              if (registration.id === x.id) {
                return {
                  ...x,
                  ...registration,
                };
              }
              return x;
            }),
          },
        },
      },
    };
  };

  neverInvoiceRegistration = registration => {
    const model = {
      ...registration,
      neverInvoicing: true,
    };
    const { details, activeInvoiceStreamId } = this.state;
    const detail = details[activeInvoiceStreamId];
    this.setState(this.updateRegistrationState(model, activeInvoiceStreamId), () => {
      api.base
        .put(
          `${config.appUrls.api}/api/registration/NeverInvoice/${registration.id}?neverInvoice=true`,
        )
        .then(
          () => {
            const { intl } = this.props;
            ToastrService.success(intl.formatMessage(messages.RegistrationMarkedAsNeverInvoice));
            this.setState(
              {
                details: {
                  ...details,
                  [activeInvoiceStreamId]: {
                    ...detail,
                    data: {
                      ...detail.data,
                      registrations: detail.data.registrations.filter(
                        x => x.id !== registration.id,
                      ),
                    },
                  },
                },
              },
              () => {
                // refresh the totals for current invoicestream
                this.refreshOpenRegistrationsForInvoiceStream(activeInvoiceStreamId);
              },
            );
          },
          error => {
            const { intl } = this.props;
            this.setState(
              this.updateRegistrationState(
                { ...registration, neverInvoicing: false },
                activeInvoiceStreamId,
              ),
            );
            handleHttpError(intl)(error);
          },
        );
    });
  };
  render() {
    const { intl, stateService } = this.props;
    const {
      items,
      invoiceScheduleDate,
      invoiceScheduleDateFocused,
      hideDevPanel,
      columnStatus,
      allRelationsSelected,
      creatingReport,
      activeRegistration,
      showEditRegistrationModal,
      showRemoveRegistrationModal,
      showNeverInvoiceRegistrationModal,
      showColumnSelectionModal,
      hideableColumns,
      assignmentsEnabled,
      initialSearchParams,
      totals,
    } = this.state;

    const displaySettings = (items || []).reduce(
      (result, item) => ({
        showDistance: result.showDistance || showColumn.distance(item),
        showForfait: result.showForfait || showColumn.fixedAmount(item),
        showInExForfait:
          result.showInExForfait ||
          showColumn.inForfaitAmount(item) ||
          showColumn.exForfaitAmount(item),
        showAdvances: result.showAdvances || showColumn.unsettledAdvances(item),
      }),
      {
        showDistance: false,
        showForfait: false,
        showInExForfait: false,
        showAdvances: false,
      },
    );
    return (
      <div className="to-invoice-screen">
        {showRemoveRegistrationModal && (
          <YesNoModal
            show
            title={intl.formatMessage(messages.RemoveRegistration)}
            message={intl.formatMessage(messages.RemoveRegistrationConfirmationMsg)}
            onNo={() =>
              this.setState({ showRemoveRegistrationModal: false, activeRegistration: undefined })
            }
            onSave={() => this.removeRegistration(activeRegistration)}
          />
        )}
        {showNeverInvoiceRegistrationModal && (
          <YesNoModal
            show
            title={intl.formatMessage(messages.NeverInvoiceRegistration)}
            message={intl.formatMessage(messages.NeverInvoiceRegistrationConfirmationMsg)}
            onNo={() =>
              this.setState({
                showNeverInvoiceRegistrationModal: false,
                activeRegistration: undefined,
              })
            }
            onSave={() => this.neverInvoiceRegistration(activeRegistration)}
          />
        )}
        {showEditRegistrationModal && (
          <RegistrationModal
            show
            registration={activeRegistration}
            registrationOptions={editRegistrationOptions}
            onSave={this.onRegistrationModified}
            onClose={() =>
              this.setState(state => ({
                ...state,
                activeRegistration: undefined,
                showEditRegistrationModal: false,
              }))
            }
          />
        )}
        {showColumnSelectionModal && (
          <CheckboxListModal
            title={intl.formatMessage(messages.SelectOverviewColumns)}
            items={hideableColumns}
            onSave={this.handleColumnSelection}
            onClose={() => this.setState({ showColumnSelectionModal: false })}
            show={showColumnSelectionModal}
            sortByLabel={false}
          />
        )}
        <SimplePanel article="19225711469596" title={intl.formatMessage(messages.ToInvoice)}>
          <ToInvoiceFilters
            filters={initialSearchParams.toInvoiceFilters}
            onFilterChange={filters => this.filtersChanged(filters)}
            totals={totals}
            countSelected={this.getCountSelectedRelationsFormatted()}
          />
        </SimplePanel>

        {items && items.length > 0 && (
          <div className="row btn-row">
            <div className="col-md-6 pull-right content-right">
              <Button
                btnClassName="btn btn-xs btn-default margin10R"
                onClick={() => this.setState({ showColumnSelectionModal: true })}
              >
                <span className="margin5R">
                  <FaCog className="react-icon" />
                </span>
                <FormattedMessage {...messages.ColumnConfig} />
              </Button>
              <Button
                btnClassName="btn btn-xs btn-default margin10R"
                onClick={() => this.createExcelReport()}
                disabled={creatingReport}
              >
                <FaFileExcel className="file-icon" />
                {intl.formatMessage(messages.ExportToExcel)}
              </Button>
              <CanUpdate on="InvoiceDraft">
                <ToInvoiceGlobalActionsButton
                  onAddToInvoiceProposal={this.billMultipleRelations}
                  disabled={this.isGlobalActionsDisabled()}
                />
              </CanUpdate>
            </div>
          </div>
        )}
        <ScrollableTable
          tableClassName="table-grid"
          loadItems={this.loadItems}
          initialSearchParams={initialSearchParams}
          items={items}
          renderRow={item => this.renderRow(item, displaySettings, intl, stateService)}
          noDataLabel={<FormattedMessage {...messages.NoDataFound} />}
        >
          <ColumnHeader
            title={
              <Checkbox onClick={this.handleSelectAllRelations} value={allRelationsSelected} />
            }
          />
          <ColumnHeader title={intl.formatMessage(messages.Relation)} />
          <ColumnHeader
            title={intl.formatMessage(messages.RelationCode)}
            visible={columnStatus.relationCode}
          />
          {assignmentsEnabled && (
            <ColumnHeader
              title={intl.formatMessage(messages.Assignment)}
              visible={columnStatus.assignment}
            />
          )}
          <ColumnHeader title={intl.formatMessage(messages.InvoicingType)} />
          <ColumnHeader title={intl.formatMessage(messages.NextScheduleDate)} />
          <ColumnHeader title={intl.formatMessage(messages.Amount)} className="text-right" />
          <ColumnHeader title={intl.formatMessage(messages.Hours)} className="text-right" />
          {displaySettings.showDistance ? (
            <ColumnHeader title={intl.formatMessage(messages.Distance)} className="text-right" />
          ) : null}
          {displaySettings.showInExForfait ? (
            <ColumnHeader
              title={intl.formatMessage(messages.RegistrationIncludedInForfait)}
              className="text-right"
            />
          ) : null}
          {displaySettings.showInExForfait ? (
            <ColumnHeader
              title={intl.formatMessage(messages.RegistrationExcludedFromForfait)}
              className="text-right"
            />
          ) : null}
          {displaySettings.showForfait ? (
            <ColumnHeader
              title={intl.formatMessage(messages.ForfaitAmount)}
              className="text-right"
            />
          ) : null}
          {displaySettings.showAdvances ? (
            <ColumnHeader
              title={intl.formatMessage(messages.UnsettledAdvances)}
              className="text-right"
            />
          ) : null}
          <ColumnHeader
            title={intl.formatMessage(messages.ToInvoiceAmount)}
            className="text-right"
          />
          <ColumnHeader
            title={intl.formatMessage(messages.Actions)}
            className="text-right actions-column"
          />
        </ScrollableTable>
      </div>
    );
  }
}

ToInvoiceContainer.propTypes = {
  intl: intlShape.isRequired,
  stateService: PropTypes.instanceOf(StateService).isRequired,
};

export default injectStateService(injectIntl(ToInvoiceContainer));
